import type { PaymentActivityDetails } from '@paintscout/api'
import { useActivityQuery } from '@paintscout/api'
import type { DialogProps } from '@paintscout/ui'
import {
  Checkbox,
  FormControlLabel,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputField,
  Spinner,
  Link
} from '@paintscout/ui'
import { useDialogs, useClientOptions } from '@paintscout/ui-business'
import React, { useCallback, useState } from 'react'
import { formatCurrency, setPayments } from '@paintscout/util/builder'
import { getPayrixCredentials } from '@paintscout/util'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import type { Payment, QuoteDocument } from 'paintscout'
import * as Sentry from '@sentry/react'
import { useQuote } from '../../context'

export interface ViewPaymentDialogProps extends DialogProps {
  activityId: string
  quote: QuoteDocument
  onDeal?: boolean
  onClose?: () => void
}

const ViewPaymentDialog = (props: ViewPaymentDialogProps) => {
  const { open, activityId, onClose: onConfirm, quote, onDeal, ...baseDialogProps } = props
  const { options } = useClientOptions()
  const { updateQuote } = useQuote()
  const { dismissDialog } = useDialogs()
  const { enqueueSnackbar } = useSnackbar()
  const dateFormat = options.options.dateFormat.momentValue

  const [payment, setPayment] = useState<Payment>()

  const handleError = (error: any) => {
    Sentry.captureException(error)
    enqueueSnackbar('Unable to fetch payment activity', { variant: 'error' })
    dismissDialog()
  }

  const { loading, data } = useActivityQuery({
    fetchPolicy: 'network-only',
    pollInterval: 120000,
    notifyOnNetworkStatusChange: false,
    variables: {
      id: activityId
    },
    onCompleted: useCallback((data) => {
      if (!data.activity) {
        handleError(new Error(`Unable to fetch payment activity with id: ${activityId}`))
      } else {
        setPayment(quote.payments?.find((payment) => payment.activityId === activityId))
      }
    }, []),
    onError: useCallback((error) => {
      handleError(error)
    }, [])
  })

  const activity = data?.activity

  function handleChange(event: any) {
    const { value, name } = event.target
    setPayment({
      ...payment,
      [name]: value
    })
  }

  function isDirty() {
    const original = quote.payments?.find((payment) => payment.activityId === activityId)?.note
    return original !== payment?.note
  }

  function handleConfirm() {
    if (isDirty()) {
      const payments = quote.payments?.map((quotePayment) =>
        quotePayment.activityId === activityId ? payment : quotePayment
      )
      const updatedQuote = setPayments({ quote, payments, options })
      updateQuote({ quote: updatedQuote })
    }

    if (onConfirm) {
      onConfirm()
    }
  }

  const paymentIntentId = (activity?.details as PaymentActivityDetails)?.providerDetails?.id
  const stripeLink = paymentIntentId ? `https://dashboard.stripe.com/payments/${paymentIntentId}` : undefined
  const payrixTxnId = (activity?.details as PaymentActivityDetails)?.providerDetails?.paymentId
  const { portalUrl } = getPayrixCredentials(options)
  const payrixLink = payrixTxnId ? `${portalUrl}/payments/profile/${payrixTxnId}/customer` : undefined
  const portalLink = payrixLink || stripeLink
  const isCanadianPayrix = payment?.provider === 'payrix' && payment?.providerDetails?.payrix?.isCanadian
  const paymentMethod = (activity?.details as PaymentActivityDetails)?.method ?? ''
  const paymentMethodWithEFT = isCanadianPayrix && paymentMethod.toLowerCase() === 'ach' ? 'EFT' : paymentMethod

  const hasSurcharge = !!payment?.surcharge && payment.surcharge > 0

  return (
    <Dialog open={open} maxWidth="md" {...baseDialogProps}>
      <DialogTitle>View Payment</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {(loading || !payment) && (
            <Grid item xs={12}>
              <Spinner />
            </Grid>
          )}
          {!!activity && !!payment && (
            <>
              <Grid item xs={12}>
                <InputField label={'Status'} value={'Paid'} readOnly disabled fullWidth />
              </Grid>
              {paymentMethodWithEFT && (
                <Grid item xs={12}>
                  <InputField label={'Type/Method'} value={paymentMethodWithEFT} readOnly disabled fullWidth />
                </Grid>
              )}
              <Grid item xs={12} sm={hasSurcharge ? 6 : 12}>
                <InputField
                  label={'Amount'}
                  value={formatCurrency({ options, value: payment?.amount - (payment?.surcharge || 0) })}
                  readOnly
                  disabled
                  fullWidth
                />
              </Grid>
              {hasSurcharge && (
                <Grid item xs={12} sm={6}>
                  <InputField
                    label={'+ Credit Card Surcharge'}
                    value={formatCurrency({ options, value: payment?.surcharge })}
                    readOnly
                    disabled
                    fullWidth
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <InputField
                  label={'Date'}
                  value={moment(activity.details.timestamp).format(dateFormat)}
                  readOnly
                  disabled
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <InputField
                  label={'Note'}
                  name={'note'}
                  value={payment?.note ?? ''}
                  fullWidth
                  multiline
                  onChange={handleChange}
                  readOnly={onDeal}
                  disabled={onDeal}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  label={'This payment is a deposit'}
                  disabled
                  control={<Checkbox name={'deposit'} checked={payment?.deposit} />}
                />
              </Grid>
              {!!portalLink && (
                <Grid item xs={12}>
                  <Link href={portalLink} target="_blank">
                    {`View this payment in ${payrixLink ? 'PaintScout Payments' : 'Stripe'}`}
                  </Link>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions
        leftButton={
          isDirty() ? (
            <Button onClick={dismissDialog} variant="textNoPadding">
              Cancel
            </Button>
          ) : null
        }
      >
        <Button type="submit" variant={'contained'} onClick={handleConfirm}>
          {isDirty() ? 'Done' : 'Ok'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ViewPaymentDialog
